import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  TimePicker,
  message,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

import { baseUrl_SPB } from '../../BaseUrl';

const AddLeaveModal = ({
  open,
  onClose,
  onSave,
  token,
  resource,
  selectedItem,
  isUserManager,
  companyId,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadLeave();
    }
  }, [selectedItem, open]);

  const loadLeave = async () => {
    try {
      if (!selectedItem?.id) {
        form.resetFields();
        form.setFieldsValue({
          dateRange: [dayjs(), dayjs()],
          startTime: dayjs('09:00', 'HH:mm'),
          endTime: dayjs('17:00', 'HH:mm'),
        });
        return;
      }

      // Fetch specific leave data by ID pattern
      const response = await axios.get(
        `${baseUrl_SPB}/api/v1/unavailability/fetchunavailabilitybyid?id=${selectedItem.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const leave = response.data;

      form.setFieldsValue({
        dateRange: [
          dayjs(leave.startDate, 'DD-MM-YYYY'),
          dayjs(leave.endDate, 'DD-MM-YYYY')
        ],
        startTime: dayjs(leave.starttime, 'HH:mm'),
        endTime: dayjs(leave.endtime, 'HH:mm'),
      });
    } catch (err) {
      console.error(err);
      message.error('Failed to load leave details.');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Constructing your exact payload matching your data definitions
      const payload = {
        id: selectedItem?.id,
        ...selectedItem,
        approvalStatus: selectedItem?.approvalStatus || 'pending',
        staffId: resource?.id || selectedItem?.staffId,
        companyId,
        startDate: values.dateRange[0].format('DD-MM-YYYY'),
        endDate: values.dateRange[1].format('DD-MM-YYYY'),
        starttime: values.startTime.format('HH:mm'),
        endtime: values.endTime.format('HH:mm'),
      };

      // Corresponds directly with the 'updateleaveapprovalstatus' structure
      await axios.post(
        `${baseUrl_SPB}/api/v1/unavailability/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(
        selectedItem?.id ? 'Leave request updated' : 'Leave request submitted'
      );

      onSave?.();
      onClose?.();
    } catch (err) {
      if (err.errorFields) return;
      console.error(err);
      message.error('Failed to save leave request.');
    } finally {
      setSaving(false);
    }
  };

  // Restricting edits if already accepted/denied
  const canEdit =
    isUserManager ||
    !selectedItem?.id ||
    !selectedItem?.approvalStatus ||
    selectedItem?.approvalStatus === '';

  return (
    <Modal
      open={open}
      title={selectedItem?.id ? 'Edit Leave Application' : 'Apply for Leave'}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={saving}
      width={500}
      okButtonProps={{
        disabled: !canEdit,
      }}
    >
      {!canEdit && (
        <div style={{ marginBottom: 16, color: '#fa8c16', fontWeight: 500 }}>
          This leave request has already been processed and cannot be edited.
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          label="Date Duration"
          name="dateRange"
          rules={[{ required: true, message: 'Please select start and end dates' }]}
        >
          <DatePicker.RangePicker 
            style={{ width: '100%' }} 
            format="DD-MM-YYYY" 
            disabled={!canEdit}
          />
        </Form.Item>

        <Form.Item
          label="Daily Start Time"
          name="startTime"
          rules={[{ required: true, message: 'Please set a start time' }]}
        >
          <TimePicker 
            format="HH:mm" 
            style={{ width: '100%' }} 
            disabled={!canEdit}
          />
        </Form.Item>

        <Form.Item
          label="Daily End Time"
          name="endTime"
          rules={[{ required: true, message: 'Please set an end time' }]}
        >
          <TimePicker 
            format="HH:mm" 
            style={{ width: '100%' }} 
            disabled={!canEdit}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddLeaveModal;